import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import Payment from 'payment'
import {
  allowClickToPay,
  setRemember,
  getRemember,
  removeRemember,
} from 'actions'
import CardPayment from 'components/CardPayment'
import SrcCardListSkeleton from 'components/SrcCardListSkeleton'
import SrcConsent from 'components/SrcConsent'
import SrcCardList from 'components/SrcCardList'
import SplitSelect from 'components/SplitSelect'
import imgCardBlue from 'static/img/card-blue.svg'
import AlternateAuthModal from 'components/AlternateAuthModal'

const initialState = {
  srcSavedCards: [],
  srcConsentAccepted: false,
  srcRememberMe: false,
  manualEntrySelected: false,
  srcCardsLoading: true,
  showCardList: true,
  hasRecognitionToken: false,
  denied: false,
  split_number: '',
  disabledSplitNumber: false,
  selectedDigitalCardId: null,
  detectedCardFlag: '',
  showAlternateAuthModal: false,
}

export class IdentifyClickToPayUser extends Component {
  state = initialState

  componentDidMount() {
    this.initializeComponent()
  }

  initializeComponent = () => {
    const stored = localStorage.getItem('srcRememberMe')
    if (stored === 'true') {
      this.setState({ srcRememberMe: true })
    } else if (stored === 'false') {
      this.setState({ srcRememberMe: false })
    }

    getRemember().then(token => {
      if (token) this.setState({ hasRecognitionToken: true })
    })

    if (this.isSrcUiEnabled()) {
      this.loadSavedCardsForSrcList()
      this.checkSplitNumber()
    }
  }

  getMcCheckoutService = () => window.mcCheckoutService

  isSrcUiEnabled = () => allowClickToPay(this.props.seller)

  loadSavedCardsForSrcList = async () => {
    try {
      this.setState({ srcCardsLoading: true })

      const savedCards = await this.getMcCheckoutService()
        .getCards()
        .catch(() => [])
      if (savedCards?.length > 0) {
        this.setState(
          {
            srcSavedCards: savedCards,
            srcCardsLoading: false,
          },
          () => {
            this.checkSplitNumber()
          },
        )
      } else {
        this.setState({
          srcSavedCards: [],
          srcCardsLoading: false,
        })
      }
    } catch (error) {
      this.setState({
        srcSavedCards: [],
        srcCardsLoading: false,
      })
    }
  }

  handleSrcManualEntry = event => {
    this.setState({
      manualEntrySelected: true,
      split_number: '',
      disabledSplitNumber: false,
      selectedDigitalCardId: null,
    })
  }

  isVisa = () => {
    return this.state.detectedCardFlag === 'visa'
  }

  isMastercard = () => {
    return this.state.detectedCardFlag === 'mastercard'
  }

  storeCardWithSrcConsent = async cardData => {
    const encryptCardResponse = await this.getMcCheckoutService().encryptCard({
      primaryAccountNumber: cardData.card_number?.replace(/\s/g, ''),
      panExpirationMonth: cardData.card_expiration_month?.padStart(2, '0'),
      panExpirationYear: cardData.card_expiration_year?.slice(-2),
      cardSecurityCode: cardData.card_cvv,
    })

    const checkoutWindow = window.open(
      '',
      `MastercardCheckout_${Date.now()}`,
      'width=480,height=600,left=720,top=240',
    )

    const checkoutWithNewCardArgs = {
      windowRef: checkoutWindow,
      encryptedCard: encryptCardResponse.encryptedCard,
      cardBrand: encryptCardResponse.cardBrand,
      recognitionTokenRequested: this.state.srcRememberMe,
      rememberMe: this.state.srcRememberMe,
      consumer: {
        emailAddress: this.props.customer?.email,
        mobileNumber: {
          phoneNumber: this.props.customer?.phone?.replace(/\D/g, ''),
          countryCode: '55',
        },
        firstName: this.props.customer?.name?.split(' ')[0],
        lastName: this.props.customer?.name?.split(' ')[1],
      },
      complianceSettings: {
        privacy: {
          acceptedVersion: 'LATEST',
          latestVersion: 'LATEST',
          latestVersionUri: this.isMastercard()
            ? process.env.REACT_APP_PRIVACY_MASTERCARD_URL
            : process.env.REACT_APP_PRIVACY_VISA_URL,
        },
        tnc: {
          acceptedVersion: 'LATEST',
          latestVersion: 'LATEST',
          latestVersionUri: this.isMastercard()
            ? process.env.REACT_APP_TERMS_MASTERCARD_URL
            : process.env.REACT_APP_TERMS_VISA_URL,
        },
      },
    }

    const checkoutResult = await this.getMcCheckoutService()
      .checkoutWithNewCard(checkoutWithNewCardArgs)
      .catch(() => null)

    checkoutWindow?.close()

    if (this.state.srcRememberMe && checkoutResult?.recognitionToken) {
      await setRemember(checkoutResult.recognitionToken)
    }

    return checkoutResult
  }

  handleSelectCard = selectedDigitalCardId => {
    this.setState({ selectedDigitalCardId })
    this.checkSplitNumber()
  }

  handleSelect = ({ target }) => {
    this.setState({
      split_number: target.value,
    })
  }

  getPaymentMethodFromSelectedCard = () => {
    if (
      this.state.selectedDigitalCardId &&
      this.state.srcSavedCards.length > 0
    ) {
      const selectedCard = this.state.srcSavedCards.find(
        card => card.srcDigitalCardId === this.state.selectedDigitalCardId,
      )

      if (selectedCard?.paymentCardDescriptor) {
        return selectedCard.paymentCardDescriptor.toLowerCase()
      }
    }

    return 'mastercard'
  }

  checkSplitNumber = () => {
    const paymentMethod = this.getPaymentMethodFromSelectedCard()
    const methodData = this.props.availableMethods.find(
      ({ name }) => name === paymentMethod,
    )

    const hasOnlyOneSplitOption = methodData && methodData.split.length === 1
    const splitNumber = hasOnlyOneSplitOption ? '1' : ''

    this.setState({
      split_number: splitNumber,
      disabledSplitNumber: !!methodData && hasOnlyOneSplitOption,
    })
  }

  shouldDisplayRememberMe = () => {
    if (this.state.hasRecognitionToken) return false
    if (localStorage.getItem('srcRememberMe') === 'true') return false

    return true
  }

  shouldShowSrcConsent = () => {
    if (!this.isSrcUiEnabled()) return false

    if (!this.state.manualEntrySelected && this.state.srcSavedCards.length > 0)
      return false

    if (!this.isVisa() && !this.isMastercard()) return false

    return true
  }

  handleCardNumberChange = cardNumber => {
    this.setState({
      detectedCardFlag: Payment.fns.cardType(cardNumber) || '',
    })
  }

  handleCheckoutWithCard = async () => {
    const checkoutWindow = window.open(
      '',
      `MastercardCheckout_${Date.now()}`,
      'width=480,height=600,left=720,top=240',
    )

    const response = await this.getMcCheckoutService()
      .checkoutWithCard({
        windowRef: checkoutWindow,
        srcDigitalCardId: this.state.selectedDigitalCardId,
      })
      .catch(() => null)

    checkoutWindow?.close()

    return response
  }

  handlePaymentDenied = (denied = true) => {
    this.setState({
      denied: denied,
      showCardList: true,
      manualEntrySelected: false,
    })
  }

  handleTryAgain = () => {
    this.setState(initialState)
    this.initializeComponent()
  }

  handleSrcSignOut = async (phoneNumber, emailAddress) => {
    try {
      const mcCheckoutService = this.getMcCheckoutService()
      await mcCheckoutService.signOut()
      await removeRemember()

      this.props.history.push({
        pathname: '/pagamento-cartao-autenticacao',
        state: { phoneNumber, emailAddress },
      })
    } catch (error) {}
  }

  render() {
    if (!this.isSrcUiEnabled()) {
      return <CardPayment {...this.props} />
    }

    return (
      <div>
        {this.state.denied === false && this.isSrcUiEnabled() && (
          <div
            style={{
              display:
                !this.state.manualEntrySelected &&
                this.state.showCardList &&
                (this.state.srcCardsLoading ||
                  this.state.srcSavedCards.length > 0)
                  ? 'block'
                  : 'none',
            }}
          >
            <div
              className="flex items-center"
              style={{ marginBottom: '16px', gap: '8px' }}
            >
              <img src={imgCardBlue} alt="logo" />
              <h2 style={{ color: '#4F4886', margin: '0px' }}>
                Cartão de crédito
              </h2>
            </div>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {this.state.srcCardsLoading &&
              this.state.srcSavedCards.length === 0 ? (
                <SrcCardListSkeleton />
              ) : (
                <>
                  <SrcCardList
                    cards={this.state.srcSavedCards}
                    onSelectCard={this.handleSelectCard}
                    onAddCardClick={this.handleSrcManualEntry}
                    onSrcSignOut={() =>
                      this.setState({ showAlternateAuthModal: true })
                    }
                  />

                  <div style={{ margin: '30px' }}>
                    <SplitSelect
                      availableMethods={this.props.availableMethods}
                      totalPrice={this.props.totalPrice}
                      paymentMethod={this.getPaymentMethodFromSelectedCard()}
                      value={this.state.split_number}
                      onChange={this.handleSelect}
                      disabled={this.state.disabledSplitNumber}
                      id="src-payment-option"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <CardPayment
          {...this.props}
          srcSavedCards={this.state.srcSavedCards}
          srcLoading={
            this.state.srcCardsLoading && !this.state.manualEntrySelected
          }
          manualEntrySelected={this.state.manualEntrySelected}
          showSrcCardList={this.state.showCardList}
          srcConsentAccepted={this.state.srcConsentAccepted}
          onCardNumberChange={this.handleCardNumberChange}
          onStoreCardWithSrcConsent={
            this.state.srcConsentAccepted
              ? cardData => this.storeCardWithSrcConsent(cardData)
              : null
          }
          makeCheckoutWithCard={this.handleCheckoutWithCard}
          onPaymentDenied={this.handlePaymentDenied}
          onTryAgain={this.handleTryAgain}
          split_number={this.state.split_number}
          onSplitNumberChange={value => this.setState({ split_number: value })}
          srcConsentElement={
            this.shouldShowSrcConsent() ? (
              <SrcConsent
                displayRememberMe={this.shouldDisplayRememberMe()}
                onConsentChange={checked =>
                  this.setState({ srcConsentAccepted: checked })
                }
                onRememberMeChange={checked =>
                  this.setState({ srcRememberMe: checked })
                }
              />
            ) : null
          }
        />

        <AlternateAuthModal
          isOpen={this.state.showAlternateAuthModal}
          onClose={() => this.setState({ showAlternateAuthModal: false })}
          onSubmit={this.handleSrcSignOut}
          loading={false}
        />
      </div>
    )
  }
}

IdentifyClickToPayUser.propTypes = {
  seller: PropTypes.object,
  customer: PropTypes.object,
  availableMethods: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      split: PropTypes.arrayOf(
        PropTypes.shape({
          split: PropTypes.number.isRequired,
          value_split: PropTypes.string.isRequired,
          value_transaction: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ),
  totalPrice: PropTypes.number,
}

const mapStateToProps = /* istanbul ignore next */ state => {
  const {
    transaction: {
      seller,
      order: {
        available_payment_methods: paymentMethods,
        total_price: totalPrice,
      },
    },
    user: customer,
  } = state

  let availableMethods = null
  if (paymentMethods) {
    const cardMethods = paymentMethods.filter(item => item.type === 'card')
    if (cardMethods.length > 0) {
      availableMethods = cardMethods[0].values
    }
  }

  return {
    seller,
    customer,
    availableMethods,
    totalPrice,
  }
}

export default withRouter(connect(mapStateToProps)(IdentifyClickToPayUser))
